/**
 * Smart Business Card — Public Viewer
 * URL: /smart/:slug
 * 23 business templates · mobile-first · floating WhatsApp CTA · OG share preview.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, MessageCircle, MapPin, Share2, Eye, Sparkles } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

// ----- Google fonts to lazy-load per template -----
const FONT_URLS = {
  'Playfair Display': 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&display=swap',
  'Merriweather':    'https://fonts.googleapis.com/css2?family=Merriweather:wght@700;900&display=swap',
  'Quicksand':       'https://fonts.googleapis.com/css2?family=Quicksand:wght@500;700&display=swap',
  'Cormorant Garamond':'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;700&display=swap',
  'Inter':           'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap',
  'Oswald':          'https://fonts.googleapis.com/css2?family=Oswald:wght@500;700&display=swap',
  'Pacifico':        'https://fonts.googleapis.com/css2?family=Pacifico&display=swap',
  'Montserrat':      'https://fonts.googleapis.com/css2?family=Montserrat:wght@500;700&display=swap',
  'Poppins':         'https://fonts.googleapis.com/css2?family=Poppins:wght@500;700&display=swap',
  'Yatra One':       'https://fonts.googleapis.com/css2?family=Yatra+One&display=swap',
  'Rajdhani':        'https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;700&display=swap',
};

const hexToRgba = (hex, alpha = 0.15) => {
  const m = hex?.replace('#', '').match(/.{1,2}/g);
  if (!m || m.length < 3) return `rgba(0,0,0,${alpha})`;
  const [r, g, b] = m.map((x) => parseInt(x, 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

const FA = ({ name, className = '' }) => (
  // We bundle FontAwesome via CDN at top of this page
  <i className={`fa-solid fa-${name} ${className}`} />
);

const SmartCardPublic = () => {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/smart-card/public/${slug}`);
        if (alive) setCard(r.data);
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || 'Card not found');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    // Inject FontAwesome CDN once
    if (!document.getElementById('fa6-cdn')) {
      const link = document.createElement('link');
      link.id = 'fa6-cdn';
      link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
      document.head.appendChild(link);
    }
    return () => { alive = false; };
  }, [slug]);

  // Inject the template's google font
  useEffect(() => {
    if (!card?.template?.font) return;
    const url = FONT_URLS[card.template.font];
    if (!url) return;
    const id = `gf-${card.template.font.replace(/\s+/g, '-')}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  }, [card?.template?.font]);

  const onShare = async () => {
    const url = `${window.location.origin}/smart/${slug}`;
    if (navigator.share) {
      try { await navigator.share({ title: card.business_name, text: card.tagline, url }); return; }
      catch (e) { /* fall through */ }
    }
    try { await navigator.clipboard.writeText(url); toast.success('Link copied'); }
    catch (e) { toast.error('Could not copy'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-slate-500 text-sm">Loading card…</div>
      </div>
    );
  }
  if (err || !card) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100 p-6 text-center" data-testid="smart-card-error">
        <Sparkles className="w-10 h-10 text-amber-400 mb-3" />
        <div className="text-2xl font-bold text-slate-800">Card not found</div>
        <div className="text-slate-500 text-sm mt-2">The link may be inactive or removed.</div>
        <Link to="/" className="mt-5 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm">Go home</Link>
      </div>
    );
  }

  const t = card.template || {};
  const accent = t.accent || '#D4AF37';
  const isDark = (t.bg || '#fff').match(/^#[0-1]/);    // crude dark-bg detector
  const textDark = t.text_dark || '#1a1a1a';
  const accentSoft = hexToRgba(accent, 0.15);
  const accentLine = hexToRgba(accent, 0.4);

  const phone = (card.phone || '').replace(/[^+\d]/g, '');
  const wa = (card.whatsapp || card.phone || '').replace(/[^\d]/g, '');
  const waLink = wa ? `https://wa.me/${wa}?text=${encodeURIComponent('Hi, I saw your Smart Card on Anant Web Portal.')}` : '';
  const callLink = phone ? `tel:${phone}` : '';
  const mapsLink = card.maps_url || (card.location_text ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(card.location_text)}` : '');

  const ogImage = card.logo_url || card.cover_image_url || '';

  return (
    <>
      <Helmet>
        <title>{card.business_name} · Smart Card</title>
        <meta name="description" content={card.tagline || card.location_text || ''} />
        <meta property="og:title" content={card.business_name} />
        <meta property="og:description" content={card.tagline || card.location_text || ''} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center p-3 sm:p-6" style={{ background: 'linear-gradient(135deg, #1e1e2e 0%, #0a0a0a 100%)' }} data-testid="smart-card-public">
        <main
          className="w-full max-w-[420px] aspect-square shadow-2xl rounded-3xl overflow-hidden relative flex flex-col"
          style={{ background: t.bg, color: textDark, fontFamily: `'${t.font || 'Inter'}', sans-serif` }}
        >
          {/* 1. Cover banner — compact */}
          <div className="h-24 w-full bg-cover bg-center relative flex-shrink-0" style={{ backgroundImage: card.cover_image_url ? `url(${card.cover_image_url})` : `linear-gradient(135deg, ${accent}, ${textDark})` }}>
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.45))' }} />
            <button onClick={onShare} data-testid="smart-card-share" className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 text-slate-800 flex items-center justify-center shadow-md">
              <Share2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* 2. Logo — overlap */}
          <div className="relative -mt-10 flex justify-center flex-shrink-0">
            <div className="w-20 h-20 rounded-full border-[3px] border-white shadow-lg overflow-hidden flex items-center justify-center" style={{ background: accentSoft }}>
              {card.logo_url ? (
                <img src={card.logo_url} alt={card.business_name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl font-bold" style={{ color: accent }}>{card.business_name?.[0] || 'A'}</span>
              )}
            </div>
          </div>

          {/* 3. Business details — compact */}
          <div className="text-center mt-1.5 px-4 flex-shrink-0">
            <h1 className="text-xl font-bold tracking-wide leading-tight" style={{ color: textDark, fontFamily: `'${t.font || 'Inter'}', serif` }} data-testid="smart-card-business-name">
              {card.business_name}
            </h1>
            {card.location_text && (
              <p className="text-[11px] mt-0.5 font-medium flex items-center justify-center gap-1" style={{ color: accent }}>
                <MapPin className="w-3 h-3" /> {card.location_text}
              </p>
            )}
            {card.tagline && (
              <p className="text-[11px] mt-1 italic leading-snug line-clamp-2" style={{ color: hexToRgba(textDark, isDark ? 0.8 : 0.6) }}>
                &ldquo;{card.tagline}&rdquo;
              </p>
            )}
          </div>

          {/* 4. Quick action buttons — compact */}
          <div className="flex justify-center gap-5 mt-2 flex-shrink-0">
            <ActionBtn href={callLink} disabled={!callLink} testid="smart-card-call">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm" style={{ background: accentSoft, color: accent }}>
                <Phone className="w-4 h-4" />
              </div>
              <span className="text-[10px] mt-0.5 font-medium" style={{ color: hexToRgba(textDark, 0.7) }}>Call</span>
            </ActionBtn>
            <ActionBtn href={waLink} disabled={!waLink} testid="smart-card-whatsapp">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-md" style={{ background: accent, color: '#fff' }}>
                <MessageCircle className="w-4 h-4" />
              </div>
              <span className="text-[10px] mt-0.5 font-medium" style={{ color: hexToRgba(textDark, 0.7) }}>WhatsApp</span>
            </ActionBtn>
            <ActionBtn href={mapsLink} disabled={!mapsLink} testid="smart-card-directions" external>
              <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm" style={{ background: accentSoft, color: accent }}>
                <MapPin className="w-4 h-4" />
              </div>
              <span className="text-[10px] mt-0.5 font-medium" style={{ color: hexToRgba(textDark, 0.7) }}>Directions</span>
            </ActionBtn>
          </div>

          {/* 5. Collections — auto-fit grid, scrolls inside card if needed */}
          {(card.collections || []).length > 0 && (
            <div className="mt-2 px-4 flex-1 min-h-0 overflow-y-auto" data-testid="smart-card-collections">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="flex-1 h-[1px]" style={{ background: accentLine }} />
                <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: accent }}>Our Collections</span>
                <span className="flex-1 h-[1px]" style={{ background: accentLine }} />
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {card.collections.slice(0, 8).map((c, i) => (
                  <div key={i} data-testid={`smart-card-collection-${i}`} className="rounded-lg p-1.5 text-center border" style={{ background: accentSoft, borderColor: accentLine }}>
                    {c.image_url ? (
                      <img src={c.image_url} alt={c.label} className="w-6 h-6 object-cover rounded mx-auto" />
                    ) : (
                      <FA name={c.icon || 'circle-dot'} className="text-sm" />
                    )}
                    <h3 className="font-medium text-[9px] mt-0.5 leading-tight truncate" style={{ color: textDark }}>{c.label}</h3>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer pinned at bottom */}
          <div className="flex items-center justify-between px-4 py-1.5 flex-shrink-0 border-t" style={{ borderColor: accentLine }}>
            <span className="text-[9px] flex items-center gap-1" style={{ color: hexToRgba(textDark, 0.4) }}>
              <Eye className="w-2.5 h-2.5" /> {card.view_count?.toLocaleString('en-IN')} views
            </span>
            <Link to="/" className="text-[9px] hover:underline" style={{ color: hexToRgba(textDark, 0.5) }}>
              Powered by <strong style={{ color: hexToRgba(textDark, 0.8) }}>Anant</strong>
            </Link>
          </div>

          {/* Floating WhatsApp CTA — inside the square card */}
          {waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="smart-card-floating-whatsapp"
              className="absolute bottom-9 right-3 w-11 h-11 rounded-full flex items-center justify-center text-xl shadow-xl hover:scale-110 transition-all z-20"
              style={{ background: '#25D366', color: '#fff' }}
              aria-label="Chat on WhatsApp"
            >
              <i className="fa-brands fa-whatsapp" />
              <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-green-300 border-2 border-white rounded-full animate-pulse" />
            </a>
          )}
        </main>
      </div>
    </>
  );
};

const ActionBtn = ({ href, children, disabled, testid, external }) => {
  if (disabled || !href) {
    return (
      <div className="flex flex-col items-center opacity-40 cursor-not-allowed" data-testid={`${testid}-disabled`}>
        {children}
      </div>
    );
  }
  return (
    <a
      href={href}
      data-testid={testid}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className="flex flex-col items-center group hover:opacity-90"
    >
      {children}
    </a>
  );
};

export default SmartCardPublic;
